import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  ColorPicker,
  Space,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "antd/lib/form/Form";
import { Locale } from "../../utils";
import { updateBlock } from "../../utils/redux/actions/CustomPage";
import { getSelectedBlock } from "../../utils/redux/reducers/CustomPage";
import ImageUpload from "../../components/media/ImageUpload";
import LinkSelector from "../../components/linkSelector/LinkSelector";
import SlateRichTextEditor from "../../components/editor/SlateRichTextEditor";
import { BLOCK_TYPES } from "./blockConfigs";
import "./BlockEditor.scss";

const i18n = Locale.getInstance();
const { Option } = Select;
const { TextArea } = Input;

const BlockEditor = () => {
  const dispatch = useDispatch();
  const selectedBlock = useSelector(getSelectedBlock);
  const [form] = useForm();

  useEffect(() => {
    if (selectedBlock) {
      form.setFieldsValue(selectedBlock.config);
    } else {
      form.resetFields();
    }
  }, [selectedBlock, form]);

  if (!selectedBlock) {
    return (
      <div className="block-editor-empty">
        <p>{i18n.t("customPage.selectBlockToEdit")}</p>
      </div>
    );
  }

  const handleValuesChange = (changedValues, allValues) => {
    dispatch(updateBlock(selectedBlock.id, allValues));
  };

  const renderBlockForm = () => {
    switch (selectedBlock.type) {
      case BLOCK_TYPES.BANNER:
        return renderBannerForm();
      case BLOCK_TYPES.PRODUCT_GRID:
        return renderProductGridForm();
      case BLOCK_TYPES.CATEGORY_GRID:
        return renderCategoryGridForm();
      case BLOCK_TYPES.NAVIGATION:
        return renderNavigationForm();
      case BLOCK_TYPES.VIDEO:
        return renderVideoForm();
      case BLOCK_TYPES.ADVERTISEMENT:
        return renderAdvertisementForm();
      case BLOCK_TYPES.TEXT:
        return renderTextForm();
      case BLOCK_TYPES.FLASH_SALE:
        return renderFlashSaleForm();
      default:
        return null;
    }
  };

  const renderBannerForm = () => {
    return (
      <>
        <Form.Item label={i18n.t("customPage.bannerItems")} name="items">
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} style={{ marginBottom: 16 }}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Form.Item
                        {...restField}
                        name={[name, "image"]}
                        label={i18n.t("customPage.image")}
                      >
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue, setFieldsValue }) => {
                            const currentImage = getFieldValue([
                              "items",
                              name,
                              "image",
                            ]);
                            const imageArray =
                              currentImage && typeof currentImage === "string"
                                ? [{ file_path: currentImage }]
                                : Array.isArray(currentImage)
                                ? currentImage
                                : [];
                            return (
                              <ImageUpload
                                images={imageArray}
                                setSelectedImages={(image) => {
                                  const items = getFieldValue("items") || [];
                                  items[name] = {
                                    ...items[name],
                                    image: image[0]?.file_path || "",
                                  };
                                  setFieldsValue({ items });
                                  handleValuesChange(
                                    {},
                                    { ...form.getFieldsValue(), items }
                                  );
                                }}
                              />
                            );
                          }}
                        </Form.Item>
                      </Form.Item>
                      <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue, setFieldsValue }) => (
                          <Form.Item
                            {...restField}
                            name={[name, "link_data"]}
                            shouldUpdate
                            label={i18n.t("customPage.linkType")}
                          >
                            <LinkSelector
                              selectedLink={
                                getFieldValue(["items", name, "link_data"]) ??
                                false
                              }
                              setSelectedLink={(link) => {
                                const items = getFieldValue("items") || [];
                                items[name] = {
                                  ...items[name],
                                  link_data: link,
                                };
                                setFieldsValue({ items });
                                handleValuesChange(
                                  {},
                                  { ...form.getFieldsValue(), items }
                                );
                              }}
                            />
                          </Form.Item>
                        )}
                      </Form.Item>
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      >
                        {i18n.t("common.delete")}
                      </Button>
                    </Space>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({ image: "", link_data: null })}
                >
                  {i18n.t("customPage.addBannerItem")}
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>
        <Form.Item
          name="autoplay"
          label={i18n.t("customPage.autoplay")}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item name="interval" label={i18n.t("customPage.interval")}>
          <InputNumber min={1000} step={1000} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="height" label={i18n.t("customPage.height")}>
          <InputNumber min={100} step={10} style={{ width: "100%" }} />
        </Form.Item>
      </>
    );
  };

  const renderProductGridForm = () => {
    return (
      <>
        <Form.Item name="title" label={i18n.t("customPage.title")}>
          <Input />
        </Form.Item>
        <Form.Item name="source" label={i18n.t("customPage.source")}>
          <Select>
            <Option value="category">
              {i18n.t("customPage.sourceCategory")}
            </Option>
            <Option value="featured">
              {i18n.t("customPage.sourceFeatured")}
            </Option>
            <Option value="discounted">
              {i18n.t("customPage.sourceDiscounted")}
            </Option>
            <Option value="custom">{i18n.t("customPage.sourceCustom")}</Option>
          </Select>
        </Form.Item>
        <Form.Item name="category_id" label={i18n.t("customPage.categoryId")}>
          <InputNumber style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="product_ids" label={i18n.t("customPage.productIds")}>
          <Input placeholder="1,2,3" />
        </Form.Item>
        <Form.Item name="columns" label={i18n.t("customPage.columns")}>
          <InputNumber min={1} max={4} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="limit" label={i18n.t("customPage.limit")}>
          <InputNumber min={1} max={50} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="show_price"
          label={i18n.t("customPage.showPrice")}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </>
    );
  };

  const renderCategoryGridForm = () => {
    return (
      <>
        <Form.Item name="title" label={i18n.t("customPage.title")}>
          <Input />
        </Form.Item>
        <Form.Item name="columns" label={i18n.t("customPage.columns")}>
          <InputNumber min={1} max={6} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="category_ids" label={i18n.t("customPage.categoryIds")}>
          <Input placeholder="1,2,3" />
        </Form.Item>
      </>
    );
  };

  const renderNavigationForm = () => {
    return (
      <div className="navigation-form" key={selectedBlock.id}>
        <Form.Item name="columns" label={i18n.t("customPage.columns")}>
          <InputNumber min={1} max={12} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item label={i18n.t("customPage.navigationIcons")} name="icons">
          <Form.List className="navigation-icons-list" name="icons">
            {(fields, { add, remove }) => (
              <>
                <div className="navigation-icons-list-items">
                  {fields.map(({ key, name, ...restField }) => (
                    <Card key={key} style={{ marginBottom: 16 }}>
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Form.Item
                          className="navigation-icon-item"
                          {...restField}
                          name={[name, "icon"]}
                          label={i18n.t("customPage.icon")}
                        >
                          <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue, setFieldsValue }) => {
                              const currentIcon = getFieldValue([
                                "icons",
                                name,
                                "icon",
                              ]);
                              const iconArray =
                                currentIcon && typeof currentIcon === "string"
                                  ? [{ file_path: currentIcon }]
                                  : Array.isArray(currentIcon)
                                  ? currentIcon
                                  : [];
                              return (
                                <ImageUpload
                                  images={iconArray}
                                  setSelectedImages={(image) => {
                                    const icons = getFieldValue("icons") || [];
                                    icons[name] = {
                                      ...icons[name],
                                      icon: image[0]?.file_path || "",
                                    };
                                    setFieldsValue({ icons });
                                    handleValuesChange(
                                      {},
                                      { ...form.getFieldsValue(), icons }
                                    );
                                  }}
                                />
                              );
                            }}
                          </Form.Item>
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, "title"]}
                          label={i18n.t("customPage.name")}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue, setFieldsValue }) => (
                            <Form.Item
                              {...restField}
                              name={[name, "link_data"]}
                              shouldUpdate
                              label={i18n.t("customPage.linkType")}
                            >
                              <LinkSelector
                                selectedLink={
                                  getFieldValue(["icons", name, "link_data"]) ??
                                  false
                                }
                                setSelectedLink={(link) => {
                                  const icons = getFieldValue("icons") || [];
                                  icons[name] = {
                                    ...icons[name],
                                    link_data: link,
                                  };
                                  setFieldsValue({ icons });
                                  handleValuesChange(
                                    {},
                                    { ...form.getFieldsValue(), icons }
                                  );
                                }}
                              />
                            </Form.Item>
                          )}
                        </Form.Item>
                        <Button
                          type="link"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        >
                          {i18n.t("common.delete")}
                        </Button>
                      </Space>
                    </Card>
                  ))}
                </div>
                <Button
                  type="dashed"
                  onClick={() => add({ icon: "", title: "", link_data: null })}
                >
                  {i18n.t("customPage.addNavigationIcon")}
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>
      </div>
    );
  };

  const renderVideoForm = () => {
    return (
      <>
        <Form.Item name="title" label={i18n.t("customPage.title")}>
          <Input />
        </Form.Item>
        <Form.Item name="source" label={i18n.t("customPage.source")}>
          <Select>
            <Option value="latest">{i18n.t("customPage.sourceLatest")}</Option>
            <Option value="featured">
              {i18n.t("customPage.sourceFeatured")}
            </Option>
            <Option value="custom">{i18n.t("customPage.sourceCustom")}</Option>
          </Select>
        </Form.Item>
        <Form.Item name="video_ids" label={i18n.t("customPage.videoIds")}>
          <Input placeholder="1,2,3" />
        </Form.Item>
        <Form.Item name="layout" label={i18n.t("customPage.layout")}>
          <Select>
            <Option value="grid">{i18n.t("customPage.layoutGrid")}</Option>
            <Option value="list">{i18n.t("customPage.layoutList")}</Option>
          </Select>
        </Form.Item>
        <Form.Item name="limit" label={i18n.t("customPage.limit")}>
          <InputNumber min={1} max={20} style={{ width: "100%" }} />
        </Form.Item>
      </>
    );
  };

  const renderAdvertisementForm = () => {
    return (
      <>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldsValue }) => {
            const currentImage = getFieldValue("image");
            const imageArray =
              currentImage && typeof currentImage === "string"
                ? [{ file_path: currentImage }]
                : Array.isArray(currentImage)
                ? currentImage
                : [];
            return (
              <Form.Item name="image" label={i18n.t("customPage.image")}>
                <ImageUpload
                  images={imageArray}
                  setSelectedImages={(image) => {
                    const imagePath = image[0]?.file_path || "";
                    setFieldsValue({ image: imagePath });
                    handleValuesChange(
                      {},
                      { ...form.getFieldsValue(), image: imagePath }
                    );
                  }}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldsValue }) => (
            <Form.Item
              name="link_data"
              shouldUpdate
              label={i18n.t("customPage.linkType")}
            >
              <LinkSelector
                selectedLink={getFieldValue("link_data") ?? false}
                setSelectedLink={(link) => {
                  setFieldsValue({
                    link_data: link,
                  });
                  handleValuesChange(
                    {},
                    { ...form.getFieldsValue(), link_data: link }
                  );
                }}
              />
            </Form.Item>
          )}
        </Form.Item>
      </>
    );
  };

  const renderTextForm = () => {
    return (
      <>
        <Form.Item name="content" label={i18n.t("customPage.content")}>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <SlateRichTextEditor
                value={getFieldValue("content")}
                onChange={(value) => {
                  setFieldsValue({ content: value });
                  handleValuesChange(
                    {},
                    { ...form.getFieldsValue(), content: value }
                  );
                }}
              />
            )}
          </Form.Item>
        </Form.Item>
        <Form.Item
          name="background_color"
          label={i18n.t("customPage.backgroundColor")}
        >
          <ColorPicker />
        </Form.Item>
        <Form.Item name="padding" label={i18n.t("customPage.padding")}>
          <InputNumber min={0} step={5} style={{ width: "100%" }} />
        </Form.Item>
      </>
    );
  };

  const renderFlashSaleForm = () => {
    return (
      <>
        <Form.Item
          name="flash_sale_id"
          label={i18n.t("customPage.flashSaleId")}
        >
          <InputNumber style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="show_countdown"
          label={i18n.t("customPage.showCountdown")}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </>
    );
  };

  return (
    <div className="block-editor">
      <Card title={i18n.t("customPage.blockSettings")}>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleValuesChange}
          initialValues={selectedBlock?.config || {}}
        >
          {renderBlockForm()}
        </Form>
      </Card>
    </div>
  );
};

export default BlockEditor;
